/**
 * ASCVD High Risk First Event Study - Self-Contained Module
 *
 * Cohort B: For adults at high risk of atherosclerotic cardiovascular disease
 * who have NOT had a heart attack or stroke yet
 */

import { defineStudy } from "./registry";
import { azascvdHighRiskScreeningQuestions } from "../../../data/pre-screener-config/AZASCVDHighRiskScreener";
import { ascvdEligibleZipCodes } from "../../../data/ascvd-study-zipcodes/ascvdZipCodes";

/**
 * Common question IDs that will be asked before study-specific questions
 * and should be filtered out to avoid duplication
 */
const COMMON_QUESTION_IDS = [
  "cardiovascular_event_history", // NEW: Routing question
  "ldl_cholesterol_check",
  "high_intensity_statin",
  "major_medical_conditions",
  "recent_medications",
  "height",
  "weight",
  "other_clinical_trials",
  "inPersonAppointments",
];

/**
 * ASCVD High Risk First Event Study - Cohort B
 *
 * Priority: 3 (Lowest priority - for prevention in high-risk patients who have NEVER had events)
 * Trial ID: DMASCVDAZD012B
 * Location: Nationwide (US)
 *
 * Key criteria:
 * - Age 50+ (male) or 55+ (female)
 * - NO history of heart attack or stroke (this is the key difference from Cohort A)
 * - LDL 100+ or not recently tested
 * - Taking high-intensity statin
 * - Complex risk factor evaluation with multiple qualifying paths
 */
export const ascvdHighRiskStudy = defineStudy({
  id: "ascvd-high-risk",
  name: "ASCVD High Risk First Event Study (Cohort B)",
  trialId: "DMASCVDAZD012B",
  priority: 3, // Lowest priority - for prevention in patients who have NEVER had events

  // Location-restricted - specific study sites
  eligibleZipCodes: Array.from(ascvdEligibleZipCodes),

  // All questions including common ones (will be filtered automatically)
  questions: azascvdHighRiskScreeningQuestions,

  // Questions to filter out (already asked in common stage)
  commonQuestionIds: COMMON_QUESTION_IDS,

  // ROUTING FILTER: Only consider this study for patients who have NEVER had cardiovascular events
  // This is critical to prevent conflicts with the Established cohort
  commonAnswerRequirements: {
    cardiovascular_event_history: ["never"],
  },

  metadata: {
    sponsor: "AstraZeneca",
    description:
      "Investigating an investigational medication for preventing major cardiovascular events in adults at high risk who have not had a heart attack or stroke",
  },
});
